/**
 * Time formatting utilities for the SAIK Dashboard.
 *
 * All timestamps from the backend are ISO 8601 UTC strings.
 * We display them in the user's local timezone with relative context.
 */

/**
 * Format a timestamp as a relative time (e.g., "2h ago", "Just now", "3d ago").
 * Falls back to absolute date for anything older than 7 days.
 */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMs < 0) return 'Just now'; // future timestamp (clock skew)
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
}

/**
 * Format a timestamp as a short relative time (e.g., "2h", "3d", "Just now").
 * Useful for compact UI spaces.
 */
export function timeAgoShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMs < 0) return 'now';
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHr < 24) return `${diffHr}h`;
  if (diffDay < 7) return `${diffDay}d`;
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

/**
 * Format a timestamp as a full date with time.
 * Example: "Aug 25, 2026, 3:42 PM"
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a timestamp as a date only.
 * Example: "Aug 25, 2026"
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format a timestamp as a short date.
 * Example: "Aug 25"
 */
export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

/**
 * Format a time only (useful for "Started 3:42 PM").
 * Example: "3:42 PM"
 */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit' });
}

/**
 * Format a timestamp with both relative and absolute context.
 * Example: "2h ago (Aug 25, 3:42 PM)" — best for audit trails and cards.
 */
export function timeAgoWithDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  const abs = d.toLocaleString('en', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' });

  if (diffMs < 0) return abs;
  if (diffMin < 1) return `Just now (${abs})`;
  if (diffMin < 60) return `${diffMin}m ago (${abs})`;
  if (diffHr < 24) return `${diffHr}h ago (${abs})`;
  if (diffDay < 7) return `${diffDay}d ago (${abs})`;
  return abs;
}

/**
 * Format a due date with relative context.
 * Example: "Due in 2d (Aug 27)" or "2d overdue (Aug 23)"
 */
export function dueDateWithContext(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDay = Math.round(diffMs / (1000 * 60 * 60 * 24));

  const abs = d.toLocaleDateString('en', { month: 'short', day: 'numeric' });

  if (diffDay < 0) return `${Math.abs(diffDay)}d overdue (${abs})`;
  if (diffDay === 0) return `Due today (${abs})`;
  if (diffDay === 1) return `Due tomorrow (${abs})`;
  if (diffDay < 7) return `Due in ${diffDay}d (${abs})`;
  return `Due ${abs}`;
}